import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '~/components/atoms/Button';
import CheckboxField from '~/components/atoms/CheckboxField';
import InputField from '~/components/atoms/InputField';
import SelectField from '../../components/atoms/SelectField';

const VisuallyHiddenField = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
`;

const validationSchema = Yup.object({
  owner_first_name: Yup.string().required('Required'),
  owner_last_name: Yup.string().required('Required'),
  owner_email: Yup.string().email('Invalid email').required('Required'),
  owner_phone_number: Yup.string().required('Required'),
  property_city: Yup.string().required('Required'),
  your_first_name: Yup.string().required('Required'),
  your_last_name: Yup.string().required('Required'),
  your_email: Yup.string().email('Invalid email').required('Required'),
  your_phone_number: Yup.string(),
  property_postcode: Yup.string(),
  message: Yup.string().max(1000, 'Too long'),
  privacy_policy: Yup.boolean().oneOf([true], 'This field must be checked'),
  recaptchaToken: Yup.string().required('Please verify that you are not a robot'),
  leave_this_empty: Yup.string(),
  name: Yup.string(),
});

export default function ReferralForm() {
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const recaptchaRef = useRef(null);
  const submitLockRef = useRef(false);

  const emptyFormValues = {
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    owner_phone_number: '',
    property_city: '',
    property_postcode: '',
    your_first_name: '',
    your_last_name: '',
    your_email: '',
    your_phone_number: '',
    message: '',
    privacy_policy: false,
    recaptchaToken: '',
    leave_this_empty: '',
    name: '',
    formStartedAt: Date.now(),
  };

  return (
    <div className="px-8 py-12 md:py-16 md:px-20 bg-white relative border border-[#EFEEEA]">
      {isSubmitted ? (
        <p className="w-full my-6 text-center green-text text-lg">
          Your referral request was correctly sent!
        </p>
      ) : (
        <Formik
          initialValues={emptyFormValues}
          validationSchema={validationSchema}
          onSubmit={async (values, formikProps) => {
            if (submitLockRef.current || submitting) return;

            submitLockRef.current = true;
            setSubmitting(true);

            try {
              const res = await fetch('/.netlify/functions/referral-submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
              });

              const data = await res.json();

              if (!res.ok) {
                if (data?.error?.toLowerCase().includes('captcha')) {
                  formikProps.setFieldTouched('recaptchaToken', true, false);
                  formikProps.setFieldError(
                    'recaptchaToken',
                    data?.error || 'Captcha failed. Please try again.'
                  );
                } else {
                  formikProps.setStatus(data?.error || 'Submission failed. Please try again.');
                }

                formikProps.setFieldValue('recaptchaToken', '');
                recaptchaRef.current?.reset();
                return;
              }

              setIsSubmitted(true);

              formikProps.resetForm({
                values: {
                  ...emptyFormValues,
                  formStartedAt: Date.now(),
                },
              });

              recaptchaRef.current?.reset();
            } catch (e) {
              formikProps.setStatus('Something went wrong. Please try again.');
              formikProps.setFieldValue('recaptchaToken', '');
              recaptchaRef.current?.reset();
            } finally {
              setSubmitting(false);
              submitLockRef.current = false;
            }
          }}
        >
          {({ setFieldValue, errors, touched, status }) => (
            <Form id="referral-form" className="grid grid-cols-12 gap-y-8">
              <VisuallyHiddenField aria-hidden="true">
                <InputField
                  divclassname="col-span-12"
                  name="leave_this_empty"
                  type="text"
                  label="Leave this empty"
                />
              </VisuallyHiddenField>

              <VisuallyHiddenField aria-hidden="true">
                <InputField
                  divclassname="col-span-12"
                  name="name"
                  type="text"
                  label="Name"
                />
              </VisuallyHiddenField>

              <InputField
                divclassname="col-span-12"
                name="owner_first_name"
                type="text"
                label="Owner First Name:"
              />

              <InputField
                divclassname="col-span-12"
                name="owner_last_name"
                type="text"
                label="Owner Last Name:"
              />

              <InputField
                divclassname="col-span-12"
                name="owner_email"
                type="text"
                label="Owner Email:"
              />

              <InputField
                divclassname="col-span-12"
                name="owner_phone_number"
                type="text"
                label="Owner Phone Number:"
              />

              <InputField
                divclassname="col-span-12"
                name="property_postcode"
                type="text"
                label="Property Postcode:"
              />

              <InputField
                divclassname="col-span-12"
                name="your_first_name"
                type="text"
                label="Your First name:"
              />

              <InputField
                divclassname="col-span-12"
                name="your_last_name"
                type="text"
                label="Your Last name:"
              />

              <InputField
                divclassname="col-span-12"
                name="your_email"
                type="text"
                label="Your Email:"
              />

              <InputField
                divclassname="col-span-12"
                name="your_phone_number"
                type="text"
                label="Your Phone Number (inc. + international code):"
              />

              <InputField
                divclassname="col-span-12"
                name="message"
                type="text"
                label="Message:"
              />

              <SelectField
                divclassname="col-span-12 md:col-span-6"
                name="property_city"
                label="Property City"
                options={[
                  { label: 'London', value: 'london' },
                  { label: 'Paris', value: 'paris' },
                ]}
              />

              <CheckboxField
                divclassname="col-span-12"
                name="privacy_policy"
                label="By submitting this form, you accept our <a href='/privacy-policy'>Privacy Policy</a>"
              />

              {status && (
                <div className="col-span-12">
                  <p className="text-red-500 text-sm">{status}</p>
                </div>
              )}

              <div className="col-span-12">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Lc5iyYsAAAAALRhTMg-dPh0ULO_UbOcdSBVxJA3"
                  onChange={(token) => {
                    setFieldValue('recaptchaToken', token || '');
                  }}
                  onExpired={() => {
                    setFieldValue('recaptchaToken', '');
                  }}
                />

                {errors.recaptchaToken && touched.recaptchaToken && (
                  <p className="text-red-500 text-sm mt-2">
                    {errors.recaptchaToken}
                  </p>
                )}
              </div>

              <div className="col-span-12">
                <Button
                  title={submitting ? 'Sending...' : 'Refer'}
                  className="w-full"
                  type="submit"
                  submitting={submitting}
                  disabled={submitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}